import { z } from 'zod';
import { applicability, type PropertyFacts, type Skill } from './registry';
import type { ComplianceKind } from './compliance-items';
import {
  complaintSummary,
  novSummary,
  recordBody,
  recorderParties,
  sf311Details,
  type FeedSource,
} from './property-feed';
import { isPermit, safetySourceFreshness, type DisplayRecord } from './property-records';
import type { PropertySummary, RecordKind, SourceCoverage, PublicSourceState } from './types';
import { PUBLIC_SOURCE_URLS } from './public-preview';
import { assessorSourceUrl } from './source-links';
import { propertyRecords } from './property-graph';
import { policies } from '../generated/policies';
import {
  isRecorderImportExcluded,
  recorderImportNotice,
  recorderPartialNotice,
} from '../skills/sf-recorder/scripts/import-policy';

/** Source-specific interpretation and presentation descriptors; no framework dependencies. */
export function sourceDisplayName(source: string): string {
  const declaredName = policies.find((policy) => policy.id === source)?.displayName;
  return (
    declaredName ??
    source.replace(/[-_]+/g, ' ').replace(/(^|\s)\p{L}/gu, (letter) => letter.toUpperCase())
  );
}

export const TIMELINE_FILTERS = [
  'all',
  'permits',
  'public-works',
  'complaints',
  '311',
  'fire',
  'recorder',
  'street-view',
] as const;
export type TimelineFilter = (typeof TIMELINE_FILTERS)[number];

export const recordFilterSources: Record<string, SourceCoverage['source'][]> = {
  permits: ['dbi'],
  'public-works': ['public-works'],
  fire: ['section604', 'fire-inspections', 'fire-violations', 'fire-compliance'],
  complaints: ['dbi'],
  '311': ['sf311'],
  recorder: ['recorder', 'subdivision'],
};

export const defaultSourceGroups = [
  { key: 'facts' as const, sources: ['assessor'] },
  { key: 'permits' as const, sources: ['dbi', 'sf311', 'public-works'] },
  {
    key: 'safety' as const,
    sources: ['section604', 'fire-inspections', 'fire-violations', 'fire-compliance'],
  },
  { key: 'history' as const, sources: ['recorder', 'subdivision'] },
];

/** Sources whose records can become action items. */
export const ITEM_SOURCES: SourceCoverage['source'][] = [
  'dbi',
  'sf311',
  'recorder',
  'fire-inspections',
  'fire-violations',
  'fire-compliance',
];

export function matchesTimelineFilter(record: DisplayRecord, filter: TimelineFilter | string) {
  switch (filter) {
    case 'street-view':
      return record.kind === 'street-view';
    case 'permits':
      return isPermit(record.kind);
    case 'public-works':
      return record.kind === 'public-works';
    case 'fire':
      return ['fire-compliance', 'fire-inspection', 'fire-violation', 'section604'].includes(
        record.kind,
      );
    case 'complaints':
      return ['complaint', 'nov'].includes(record.kind);
    case '311':
      return record.kind === '311';
    case 'recorder':
      return ['recorder', 'subdivision'].includes(record.kind);
    default:
      // Imagery captures are browsed through their own filter and the rail;
      // in the full feed they would bury the city records.
      return record.kind !== 'street-view';
  }
}

export function complianceSource(kind: ComplianceKind): FeedSource {
  switch (kind) {
    case '311':
      return 'sf311';
    case 'fire-violation':
    case 'fire-inspection':
    case 'fire-filing':
      return 'sffd';
    case 'lien':
      return 'recorder';
    case 'tax':
      return 'treasurer';
    default:
      return 'dbi';
  }
}

const recordGlyphs = {
  'street-view': 'map',
  building: 'hammer',
  electrical: 'lightning',
  plumbing: 'water',
  complaint: 'question',
  nov: 'alert',
  recorder: 'scroll',
  subdivision: 'branch',
  '311': 'megaphone',
  section604: 'checklist',
  'fire-inspection': 'flame',
  'fire-violation': 'flame',
  'fire-compliance': 'file-check',
  assessor: 'landmark',
  'tax-bill': 'receipt',
  'public-works': 'traffic-cone',
} as const;
export type RecordGlyph = (typeof recordGlyphs)[keyof typeof recordGlyphs] | 'file';
export function recordKindGlyph(kind: RecordKind): RecordGlyph {
  return recordGlyphs[kind] ?? 'file';
}

export type SourceMarkDescriptor = {
  image?: string;
  letters?: string;
  glyph?: RecordGlyph;
  recordGlyph?: boolean;
};
const sourceMarks: Record<FeedSource, SourceMarkDescriptor> = {
  google: { image: '/images/sources/google-maps.png' },
  sf311: { image: '/images/sources/sf311.png' },
  dbi: { letters: 'DBI', recordGlyph: true },
  sffd: { letters: 'SFFD', glyph: 'flame' },
  'public-works': { letters: 'SFPW', glyph: 'traffic-cone' },
  planning: { letters: 'PLN', glyph: 'branch' },
  treasurer: { letters: 'TAX', glyph: 'landmark' },
  recorder: { letters: 'REC', glyph: 'scroll' },
};
export function sourceMark(source: FeedSource): SourceMarkDescriptor {
  return sourceMarks[source];
}

/** A source with unresolved prerequisites remains available to be checked. */
export function hasApplicableRecordSources(skills: Skill[], facts: PropertyFacts) {
  return skills.some(
    ({ policy }) =>
      policy.kind === 'source' &&
      policy.audiences.includes('saved-property') &&
      policy.outputs.some((output) => output !== 'street-view') &&
      applicability(policy, facts).status !== 'not-applicable',
  );
}

const compatibilitySources: Record<string, SourceCoverage['source']> = {
  'sf-dbi': 'dbi',
  'sf-assessor': 'assessor',
  'sf-recorder': 'recorder',
  'sf-public-works': 'public-works',
  'sf-subdivision': 'subdivision',
  'sf-311': 'sf311',
  'sf-section604': 'section604',
  'sf-fire-inspections': 'fire-inspections',
  'sf-fire-violations': 'fire-violations',
  'sf-fire-compliance': 'fire-compliance',
};
export type DisplaySourceCoverage = {
  source: string;
  sourceLabel?: string;
  sourceUrl: string;
  status: PublicSourceState['status'] | 'partial';
  freshness?: ReturnType<typeof safetySourceFreshness>;
  checkedAt?: string;
  errors?: string[];
};

/** Every source can describe its coverage, including sources with no legacy municipal view. */
export function propertySourceCoverage(summary: PropertySummary): DisplaySourceCoverage[] {
  const sources: DisplaySourceCoverage[] = summary.preview
    ? [
        ...new Set([
          ...Object.keys(PUBLIC_SOURCE_URLS),
          ...Object.keys(summary.preview.sourceStates),
        ]),
      ].map((source) => ({
        source,
        sourceUrl: PUBLIC_SOURCE_URLS[source as SourceCoverage['source']] ?? '',
        status:
          summary.preview?.sourceStates[source as SourceCoverage['source']]?.status ?? 'pending',
      }))
    : (summary.health.sources ?? []).map((source) => ({ ...source }));
  for (const source of sources) {
    const legacy = source.source as SourceCoverage['source'];
    source.freshness = safetySourceFreshness(summary, legacy);
    if (legacy === 'dbi') source.sourceUrl = summary.sourceUrl || source.sourceUrl;
    if (legacy === 'assessor')
      source.sourceUrl = assessorSourceUrl(`${summary.block}${summary.lot}`);
  }
  const represented = new Set(sources.map((source) => source.source));
  for (const coverage of summary.runtime?.coverage ?? []) {
    const source = compatibilitySources[coverage.source] ?? coverage.source;
    if (coverage.status === 'not-applicable') continue;
    if (represented.has(source)) {
      const existing = sources.find((item) => item.source === source)!;
      if (coverage.sourceLabel) existing.sourceLabel = coverage.sourceLabel;
      continue;
    }
    represented.add(source);
    sources.push({
      source,
      sourceLabel: coverage.sourceLabel,
      sourceUrl: coverage.sourceUrl ?? '',
      status: coverage.status,
      checkedAt: coverage.checkedAt,
      errors: coverage.errors,
    });
  }
  return sources;
}

const CODE_REFERENCE =
  /\b(SF(?:BC|EBC|FC|HC|EC|PC|MC))\s+(?:(?:SEC(?:TION)?\.?)\s*)?(\d+[A-Za-z]?(?:\.\d+[A-Za-z]?)*(?:\([A-Za-z0-9]+\))*)\b/gi;
const CODE_LIBRARY = 'https://codelibrary.amlegal.com/codes/san_francisco/latest/overview';
const EXACT_CODE_LINKS = new Map([
  [
    'SFFC 1103.7.6.1',
    'https://codelibrary.amlegal.com/codes/san_francisco/latest/sf_fire/0-0-0-48405',
  ],
]);
export type CitationSpan = { text: string; href?: string };
/** Destinations are skill-authored rather than supplied by generated prose. */
export function cityCodeSpans(text: string): CitationSpan[] {
  const spans: CitationSpan[] = [];
  let cursor = 0;
  for (const match of text.matchAll(CODE_REFERENCE)) {
    if (match.index > cursor) spans.push({ text: text.slice(cursor, match.index) });
    spans.push({
      text: match[0],
      href: EXACT_CODE_LINKS.get(match[0].toUpperCase()) ?? CODE_LIBRARY,
    });
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) spans.push({ text: text.slice(cursor) });
  return spans;
}

const PhotoUrlSchema = z.url().startsWith('https://spot-sf-res.cloudinary.com/image/upload/');
export const ReportPhotosSchema = z.object({
  photos: z.array(z.object({ url: PhotoUrlSchema, thumbnailUrl: PhotoUrlSchema })),
});
export type ReportPhoto = z.infer<typeof ReportPhotosSchema>['photos'][number];

export function supplementaryRecordContent(record: DisplayRecord, headline: string) {
  const report = record.kind === '311' ? sf311Details(record) : null;
  return {
    parties:
      record.kind === 'recorder'
        ? recorderParties((record.pages?.[0]?.fields.Names ?? '').split(' · ').filter(Boolean))
        : null,
    report,
    reportBody:
      recordBody(record) ||
      (report
        ? [report.description || report.details, report.type]
            .filter((part) => part && !headline.includes(part))
            .join(' · ')
        : ''),
    nov: record.kind === 'nov' ? novSummary(record) : null,
    complaintOutcome:
      record.kind === 'complaint' && !record.ai?.outcome ? complaintSummary(record).outcome : null,
  };
}

/** Client-safe source identity validation without importing the PDF implementation. */
export function recordPreviewSourceId(record: DisplayRecord) {
  return record.kind === 'recorder' &&
    record.recorderDocumentId &&
    /^\d{1,20}$/.test(record.recorderDocumentId) &&
    /^[A-Za-z0-9-]{1,40}$/.test(record.id)
    ? record.recorderDocumentId
    : null;
}

export function storedPreviewRecordKey(sourceKey: string) {
  return sourceKey.startsWith('recorder:') ? sourceKey.slice('recorder:'.length) : null;
}
export function displayArchivedDocument(sourceKey: string) {
  return !sourceKey.startsWith('311:');
}
type ArchivedDocument = {
  id: string;
  source_key: string;
  status: string;
  category: string;
  source_page_count?: number | null;
  imported_page_count?: number | null;
  mime_type: string | null;
  last_error: string | null;
  thumbnail_status?: string | null;
};
export function archivedDocumentNotices(document: ArchivedDocument, newest: ArchivedDocument) {
  const partialNotice = recorderPartialNotice(document);
  return {
    partialNotice,
    storageNotice:
      document.id !== newest.id
        ? [
            isRecorderImportExcluded(newest)
              ? `Previous stored copy · ${recorderImportNotice(newest.source_page_count!)}`
              : 'Previous stored copy · latest version unavailable',
            partialNotice,
          ]
            .filter(Boolean)
            .join(' · ')
        : document.status === 'stored'
          ? [
              partialNotice ??
                (document.category === 'tax' && document.mime_type === 'text/html'
                  ? 'Archived bill page · official PDF not provided'
                  : 'Stored copy'),
              // A check settles without this document's pages; say so on the document itself.
              document.thumbnail_status === 'unavailable' ? 'preview and text unavailable' : null,
            ]
              .filter(Boolean)
              .join(' · ')
          : (document.last_error ?? 'Download pending'),
  };
}

const summarizedPublicWorksFields = new Set(['Location scope', 'Location note']);
export function isSummarizedRecordField(kind: RecordKind, field: string) {
  return kind === 'public-works' && summarizedPublicWorksFields.has(field);
}

/** Retain specialized parity views while every new skill gets a generic record renderer. */
export function additionalPropertyRecords(summary: PropertySummary) {
  const legacy = propertyRecords(summary);
  return (summary.runtime?.records ?? []).filter((descriptor) => {
    const entity = summary.runtime?.entities.find(
      (entity) => entity.type === descriptor.entity.type && entity.key === descriptor.entity.key,
    );
    return !legacy.some(
      (record) =>
        record.kind === descriptor.category &&
        record.sourceUrl === descriptor.sourceUrl &&
        entity?.identifiers.some((identifier) => identifier.value === record.id),
    );
  });
}

export {
  propertyBriefModel,
  timelineView,
  propertyTimelineGroups,
  groupPastPublicWorks,
  recordUpdatedAt,
  recordOpenedAt,
  nextUnpaid,
  type OpenItem,
} from './property-view-models';

export {
  assessmentFieldEntries,
  assessmentView,
  assessmentAmount,
  assessmentValues,
  assessmentYear,
  assessmentYoy,
  assessmentHistory,
} from './assessment-data';
export {
  assessorFactModel,
  assessmentValueModel,
  ownershipFactModel,
  parcelFactModel,
  propertyFactCardPriority,
} from './property-fact-models';

/** Unknown sources must not disappear from the action dashboard's coverage warning. */
export function actionCoverageGaps(unavailable: string[]) {
  const legacy = new Set<string>(Object.values(compatibilitySources));
  return unavailable.filter(
    (source) => ITEM_SOURCES.includes(source as SourceCoverage['source']) || !legacy.has(source),
  );
}
